import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import SheetContainer, { CloseButton, Content, Header, Heading, Sheet } from '../../mobile/structural/sheet';
import type { UseSheetReturnType } from '../../mobile/structural/sheet/hooks/useSheet';
import useSheet from '../../mobile/structural/sheet/hooks/useSheet';
import SheetSearchBar from '../../mobile/structural/sheet/searchBar/SheetSearchBar';
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';
import { focusFakeInput, holdFocus } from '../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { useTranslator } from '../../components/i18n/useTranslator';
import FormulaDropdownListItem from './FormulaDropdownListItem';
import useFormulaPickerController from './useFormulaPickerController';
import type { FormulaPickerRef } from './useFormulaPickerController';

import './TableFormulaSheet.scss';

type TableFormulaSheetProps = {
    addFormulaToCell: (formula: string, replacePrevCharacters: number) => void;
    formulaPickerRef: MutableRefObject<FormulaPickerRef | null>;
    refocusCellEditor: () => void;
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
};

type TableFormulaSheetContentProps = Omit<TableFormulaSheetProps, 'formulaPickerRef'> & {
    sheetProps: UseSheetReturnType;
};

export const TableFormulaSheetContent = forwardRef<FormulaPickerRef, TableFormulaSheetContentProps>(
    ({ show, setShow, addFormulaToCell, refocusCellEditor, sheetProps }, ref) => {
        const translateTableToolbar = useTranslator('toolbar', 'tools.editing.table');

        const { dispatchCloseSheet, dispatchOpenSheet, isSheetOpen, sheetContentRef } = sheetProps;

        const [isScrolled, setIsScrolled] = useState(false);

        const searchInputRef = useRef<HTMLInputElement>(null);

        useLayoutEffect(() => {
            if (!isSheetOpen) return;
            if (!sheetContentRef.current) return;

            focusFakeInput({ x: 0, y: 0 }, sheetContentRef.current);
        }, [isSheetOpen, sheetContentRef]);

        useEffect(() => {
            if (isSheetOpen === show) return;

            if (show) {
                dispatchOpenSheet();
                return;
            }

            holdFocus();
            dispatchCloseSheet();
        }, [dispatchCloseSheet, dispatchOpenSheet, isSheetOpen, show]);

        const {
            focusedIndex,
            formulaPickerData,
            formulaQuery,
            hideFormulaPicker,
            selectFormula,
            setFormulaPickerQuery,
        } = useFormulaPickerController({ show, setShow, addFormulaToCell, replaceFormulaQueryOnInsert: false });

        useImperativeHandle(ref, () => ({ setFormulaPickerQuery, hideFormulaPicker }), [
            hideFormulaPicker,
            setFormulaPickerQuery,
        ]);

        const onOpenTransitionEnd = useCallback(() => {
            searchInputRef.current?.focus();
        }, []);

        const onCloseTransitionEnd = useCallback(() => {
            hideFormulaPicker();
            refocusCellEditor();
        }, [hideFormulaPicker, refocusCellEditor]);

        return (
            <SheetContainer
                className="TableFormulaSheet"
                {...sheetProps}
                defaultSnapPoint={1}
                onCloseTransitionEnd={onCloseTransitionEnd}
                onOpenTransitionEnd={onOpenTransitionEnd}
            >
                <Sheet>
                    <div data-table-util>
                        <Header>
                            <Heading>{translateTableToolbar('formula')}</Heading>
                            <CloseButton onClick={hideFormulaPicker} />
                        </Header>
                        <SheetSearchBar
                            searchFilter={formulaQuery}
                            setSearchFilter={setFormulaPickerQuery}
                            enterKeyHint="search"
                            inputRef={searchInputRef}
                            showFade={isScrolled}
                        />
                        <Content
                            className="formula-list-content"
                            onScroll={(event) => setIsScrolled((event.target as HTMLDivElement).scrollTop > 0)}
                        >
                            <ul className="formula-list">
                                {formulaPickerData.map((formula, index) => (
                                    <FormulaDropdownListItem
                                        formulaQuery={formulaQuery}
                                        focusedIndex={focusedIndex}
                                        key={formula}
                                        formula={formula}
                                        index={index}
                                        onSelectFormula={selectFormula}
                                    />
                                ))}
                            </ul>
                        </Content>
                    </div>
                </Sheet>
            </SheetContainer>
        );
    },
);

const TableFormulaSheet: React.FC<TableFormulaSheetProps> = ({ formulaPickerRef, ...otherProps }) => {
    const formulaSheetProps = useSheet(SheetId.TableFormulaTool);

    return <TableFormulaSheetContent ref={formulaPickerRef} {...otherProps} sheetProps={formulaSheetProps} />;
};

export default TableFormulaSheet;
